import { X } from "lucide-react";

import { toTitleCase } from "@/utils/string";

import { ReactComponent as DragHandleIcon } from "@assets/svg/dots-grid.svg";

import { Button } from "@components/ui/button";

import { cn } from "@utils/ui";

type Section = {
  id: string;
  name: string;
  order: number;
};

type DraggableItemProps = {
  section: Section;
  index: number;
  onRemove: (id: string) => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent, index: number) => void;
  onDrop: (e: React.DragEvent, dropIndex: number) => void;
  isDragging: boolean;
  isOver: boolean;
};

export const DraggableItem = ({
  section,
  index,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging,
  isOver,
}: DraggableItemProps) => {
  return (
    <div className="relative">
      {isOver && (
        <div className="absolute -top-1.5 left-0 right-0 h-0.5 bg-accent-700" />
      )}
      <div
        draggable
        onDragStart={(e) => onDragStart(e, index)}
        onDragEnd={onDragEnd}
        onDragOver={(e) => onDragOver(e, index)}
        onDrop={(e) => onDrop(e, index)}
        className={cn(
          "flex cursor-move items-center gap-3 rounded-lg border bg-background-white p-2 transition-all",
          isDragging && "scale-95 opacity-30",
          isOver && "border-accent shadow-md",
          !isDragging && "hover:shadow-sm"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing">
            <DragHandleIcon className="size-4 opacity-60" />
          </div>
          <div className="flex size-6 items-center justify-center rounded-full bg-accent-800 text-xs font-semibold text-primary-foreground">
            {index + 1}
          </div>
        </div>

        <div className="flex-1">
          <span className="text-sm font-medium">{toTitleCase(section.name)}</span>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => onRemove(section.id)}
          className="size-5 p-0 hover:bg-transparent hover:text-destructive"
        >
          <X className="size-4" />
        </Button>
      </div>
    </div>
  );
};
